/**
 * Jukebox modal: a flashy DOM overlay that lists the built-in songs +
 * the user's own tracks, shows a tracker-style 16-step timeline for the
 * selected song, animates a playhead + EQ visualizer, and (when the
 * pencil button is engaged) lets the user paint cells, fork built-ins,
 * rename, change BPM, clear, or delete user songs.
 *
 * Self-contained: the only outside hook is `onClose`, which the host
 * uses to restore its own audio mute state. All preview audio + the rAF
 * loop are owned by the controller returned from `open()`.
 */

import { Music, getSongs, type Song } from '../../../audio/Music.js';
import * as UserSongs from '../../../audio/UserSongs.js';
import type { UserSong } from '../../../audio/UserSongs.js';
import {
  ROOTS, SCALES, getScale, nearestInScale,
  type RootName, type ScaleDef,
} from '../../../audio/Scales.js';

export interface JukeboxController {
  /** Tear down preview audio + rAF + DOM. Idempotent. */
  close(): void;
}

export interface JukeboxOptions {
  /** Invoked exactly once when the overlay is dismissed (Esc, Close
   *  button, or backdrop click). The argument is the song that was
   *  audibly playing in the jukebox at dismiss time, or `null` if the
   *  user had stopped preview before closing. The host uses it to make
   *  level music continue playing the same track the player just chose. */
  onClose: (picked: Song | null) => void;
}

export function openJukeboxModal(opts: JukeboxOptions): JukeboxController {
  const builtins = getSongs();
  let userSongs: UserSong[] = UserSongs.listUserSongs();
  /** True when the side panel + cell painting are armed. Toggled by the
   *  pencil button in the header. */
  let editMode = false;
  /** Last-used pitch per channel — remembered so newly placed notes
   *  default to whatever the user was most recently working with, the
   *  way trackers / piano rolls behave. */
  const lastPitch: { lead: number; bass: number; drums: 'k' | 's' | 'h' } = {
    lead: 72, bass: 48, drums: 'k',
  };
  /** Allowed pitch range per channel (matches what the synth will play). */
  const PITCH_RANGE = { lead: { lo: 48, hi: 96 }, bass: { lo: 24, hi: 60 } } as const;
  const DRUM_CYCLE: ReadonlyArray<'k' | 's' | 'h'> = ['k', 's', 'h'];

  // ---- Scale filter --------------------------------------------------------
  // When the user picks a scale (root + mode), pitch changes from drag /
  // wheel snap to the nearest in-scale note instead of moving by raw
  // semitones. Default is the no-op "Chromatic" entry so existing
  // muscle-memory keeps working until the user opts in.
  const SCALE_PREF_KEY = 'shkr.jukebox.scale.v1';
  let scaleRoot: RootName = 'C';
  let scaleKey = 'none';
  try {
    const raw = localStorage.getItem(SCALE_PREF_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as { root?: string; scale?: string };
      if (parsed.root && (ROOTS as ReadonlyArray<string>).includes(parsed.root)) {
        scaleRoot = parsed.root as RootName;
      }
      if (parsed.scale) scaleKey = parsed.scale;
    }
  } catch { /* ignore — keep defaults */ }
  const currentScale = (): ScaleDef => getScale(scaleKey);
  /** Apply the active scale (lead/bass only — drums are unpitched). */
  const snapToScale = (channel: 'lead' | 'bass' | 'drums', n: number): number => {
    if (channel === 'drums') return n;
    return nearestInScale(n, scaleRoot, currentScale());
  };
  const persistScale = (): void => {
    try { localStorage.setItem(SCALE_PREF_KEY, JSON.stringify({ root: scaleRoot, scale: scaleKey })); }
    catch { /* quota — silent */ }
  };
  /**
   * Re-snap every existing pitched note in the current user song to the
   * active scale. Mutates the song's lead/bass arrays in place (the Music
   * scheduler reads `this.song.X[step]` each tick, so the reference must
   * stay stable). Skips zeros (rests) and built-in tracks (read-only).
   */
  const resnapCurrentSong = (): void => {
    if (!currentSong || !isUserIdx(currentIdx)) return;
    const sc = currentScale();
    if (sc.intervals.length === 0) return; // chromatic = no-op
    const us = currentSong as UserSong;
    let changed = false;
    for (let i = 0; i < us.lead.length; i++) {
      const v = us.lead[i] ?? 0;
      if (v <= 0) continue;
      const next = nearestInScale(clampPitch('lead', v), scaleRoot, sc);
      if (next !== v) { us.lead[i] = next; changed = true; }
    }
    for (let i = 0; i < us.bass.length; i++) {
      const v = us.bass[i] ?? 0;
      if (v <= 0) continue;
      const next = nearestInScale(clampPitch('bass', v), scaleRoot, sc);
      if (next !== v) { us.bass[i] = next; changed = true; }
    }
    lastPitch.lead = nearestInScale(lastPitch.lead, scaleRoot, sc);
    lastPitch.bass = nearestInScale(lastPitch.bass, scaleRoot, sc);
    if (changed) {
      UserSongs.update(us);
      buildTracker(us);
    }
  };

  /** Combined list = built-ins followed by user songs. The numeric index
   *  used by the cards array is global. */
  const allSongs = (): Song[] => [...builtins, ...userSongs];
  const isUserIdx = (i: number): boolean => i >= builtins.length;

  let currentIdx = -1;
  let currentSong: Song | null = null;
  let cards: HTMLButtonElement[] = [];
  let leadCells: HTMLDivElement[] = [];
  let bassCells: HTMLDivElement[] = [];
  let drumCells: HTMLDivElement[] = [];
  let stepHeaderCells: HTMLDivElement[] = [];

  let previewMusic: Music | null = null;
  let rafId: number | null = null;
  let closed = false;

  const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
  const midiToName = (n: number): string => {
    if (n <= 0) return '';
    const name = NOTE_NAMES[n % 12]!;
    const oct = Math.floor(n / 12) - 1;
    return `${name}${oct}`;
  };
  const moodFor = (s: Song): string => {
    const fastness = s.bpm >= 116 ? 'driving' : s.bpm >= 96 ? 'mid' : 'mellow';
    const bassNotes = s.bass.filter((n: number) => n > 0);
    const bassMin = bassNotes.length > 0 ? Math.min(...bassNotes) : 48;
    const tone = bassMin <= 38 ? 'deep' : bassMin <= 43 ? 'warm' : 'bright';
    return `${tone} \u00b7 ${fastness} \u00b7 ${s.bpm} bpm`;
  };

  // ----- Overlay + panel ---------------------------------------------------
  const overlay = document.createElement('div');
  Object.assign(overlay.style, {
    position: 'fixed', inset: '0', zIndex: '120',
    background: 'radial-gradient(ellipse at top, rgba(80,30,140,0.72), rgba(8,4,20,0.92) 70%)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 'env(safe-area-inset-top, 0px) env(safe-area-inset-right, 0px) env(safe-area-inset-bottom, 0px) env(safe-area-inset-left, 0px)',
    backdropFilter: 'blur(4px)',
  } as Partial<CSSStyleDeclaration>);

  const close = (): void => {
    if (closed) return;
    closed = true;
    if (rafId !== null) { cancelAnimationFrame(rafId); rafId = null; }
    // Snapshot the picked song *before* tearing down preview audio so
    // the host can hand it off to the level player.
    const picked: Song | null = (currentIdx >= 0 && currentSong) ? currentSong : null;
    if (previewMusic) { previewMusic.stop(); previewMusic = null; }
    document.removeEventListener('keydown', keyHandler);
    if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
    opts.onClose(picked);
  };

  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });

  // Detect narrow viewports (phones in portrait). At <= 720px we collapse
  // the two-column grid into a single vertical stack so the tracker pane
  // and the BUILT-IN list each get the full panel width instead of fighting
  // for ~120px each. Captured once at open time — the modal is short
  // enough that an in-place orientation change is rare; reopening picks
  // up the new layout.
  const narrow = typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(max-width: 720px)').matches;

  const panel = document.createElement('div');
  Object.assign(panel.style, narrow ? {
    width: 'min(1000px, 100vw)', height: 'min(820px, 100vh)',
    background: 'linear-gradient(180deg, #1a0f2e 0%, #120822 100%)',
    border: '1px solid #6644aa', borderRadius: '0',
    boxShadow: '0 20px 60px rgba(0,0,0,0.6), 0 0 80px rgba(170,119,221,0.18) inset',
    color: '#eee', font: '13px system-ui, sans-serif',
    display: 'grid', gridTemplateColumns: '1fr',
    gridTemplateRows: 'auto minmax(120px, 28%) 1fr auto',
    gridTemplateAreas: '"header" "list" "tracker" "footer"',
    overflow: 'hidden',
  } : {
    width: 'min(1000px, 96vw)', height: 'min(680px, 94vh)',
    background: 'linear-gradient(180deg, #1a0f2e 0%, #120822 100%)',
    border: '1px solid #6644aa', borderRadius: '14px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.6), 0 0 80px rgba(170,119,221,0.18) inset',
    color: '#eee', font: '13px system-ui, sans-serif',
    display: 'grid', gridTemplateColumns: '240px 1fr', gridTemplateRows: 'auto 1fr auto',
    gridTemplateAreas: '"header header" "list tracker" "footer footer"',
    overflow: 'hidden',
  } as Partial<CSSStyleDeclaration>);

  // ----- Header ------------------------------------------------------------
  const header = document.createElement('div');
  Object.assign(header.style, {
    gridArea: 'header',
    padding: narrow ? '10px 12px' : '14px 18px',
    background: 'linear-gradient(135deg, #4a1f6e, #2a4a8a)',
    borderBottom: '1px solid #6644aa',
    display: 'flex', alignItems: 'center', gap: narrow ? '8px' : '14px',
    flexWrap: narrow ? 'wrap' : 'nowrap',
  } as Partial<CSSStyleDeclaration>);
  const titleCol = document.createElement('div');
  Object.assign(titleCol.style, { flex: '1', minWidth: '0' } as Partial<CSSStyleDeclaration>);
  const eyebrow = document.createElement('div');
  eyebrow.textContent = 'JUKEBOX  \u00b7  TRACKER';
  Object.assign(eyebrow.style, {
    fontSize: '10px', letterSpacing: '3px', opacity: '0.75', fontWeight: '700',
  } as Partial<CSSStyleDeclaration>);
  const nowTitle = document.createElement('div');
  nowTitle.textContent = '\u2014 Pick a track \u2014';
  Object.assign(nowTitle.style, {
    fontSize: '20px', fontWeight: '800', marginTop: '2px',
    textShadow: '0 2px 8px rgba(0,0,0,0.4)',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  } as Partial<CSSStyleDeclaration>);
  const meta = document.createElement('div');
  Object.assign(meta.style, { fontSize: '11px', opacity: '0.7', marginTop: '3px' } as Partial<CSSStyleDeclaration>);
  titleCol.append(eyebrow, nowTitle, meta);

  const editBtn = document.createElement('button');
  editBtn.textContent = '\u270E Edit';
  editBtn.title = 'Toggle editor';
  Object.assign(editBtn.style, {
    padding: '8px 12px', borderRadius: '6px',
    background: 'rgba(255,255,255,0.08)', color: '#fff',
    border: '1px solid rgba(255,255,255,0.25)',
    cursor: 'pointer', font: 'inherit', fontWeight: '600', flex: '0 0 auto',
  } as Partial<CSSStyleDeclaration>);

  const transport = document.createElement('button');
  transport.textContent = '\u25B6';
  Object.assign(transport.style, {
    width: '44px', height: '44px', borderRadius: '50%',
    background: 'rgba(255,255,255,0.12)', color: '#fff',
    border: '1px solid rgba(255,255,255,0.3)',
    cursor: 'pointer', font: '18px system-ui',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto',
  } as Partial<CSSStyleDeclaration>);

  const eqCanvas = document.createElement('canvas');
  eqCanvas.width = 240; eqCanvas.height = 56;
  Object.assign(eqCanvas.style, {
    width: '160px', height: '40px',
    background: 'rgba(0,0,0,0.32)', borderRadius: '6px',
    border: '1px solid rgba(255,255,255,0.12)', flex: '0 0 auto',
    display: narrow ? 'none' : 'block',
  } as Partial<CSSStyleDeclaration>);
  header.append(titleCol, editBtn, transport, eqCanvas);

  // ----- List sidebar ------------------------------------------------------
  const list = document.createElement('div');
  Object.assign(list.style, {
    gridArea: 'list',
    overflowY: 'auto', padding: '8px',
    borderRight: narrow ? 'none' : '1px solid #2a1a4a',
    borderBottom: narrow ? '1px solid #2a1a4a' : 'none',
    background: 'rgba(0,0,0,0.18)',
    display: 'flex', flexDirection: 'column', gap: '4px',
  } as Partial<CSSStyleDeclaration>);

  // ----- Tracker pane ------------------------------------------------------
  const tracker = document.createElement('div');
  Object.assign(tracker.style, {
    gridArea: 'tracker',
    padding: '14px', overflow: 'auto',
    display: 'flex', flexDirection: 'column', gap: '10px',
    minWidth: '0',
  } as Partial<CSSStyleDeclaration>);

  // ----- Footer ------------------------------------------------------------
  const footer = document.createElement('div');
  Object.assign(footer.style, {
    gridArea: 'footer',
    padding: '10px 14px',
    borderTop: '1px solid #3a2a55',
    display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'space-between',
    background: 'rgba(0,0,0,0.25)',
  } as Partial<CSSStyleDeclaration>);
  const hint = document.createElement('div');
  Object.assign(hint.style, { fontSize: '11px', opacity: '0.65' } as Partial<CSSStyleDeclaration>);
  const copyJsonBtn = document.createElement('button');
  copyJsonBtn.textContent = 'Copy JSON';
  copyJsonBtn.disabled = true;
  Object.assign(copyJsonBtn.style, {
    padding: '8px 14px', background: 'rgba(255,255,255,0.07)', color: '#bbb',
    border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px',
    cursor: 'default', font: 'inherit', fontSize: '12px',
    transition: 'background 0.15s',
  } as Partial<CSSStyleDeclaration>);
  /** Serialize a song to JSON with arrays inline, everything else indented. */
  const songToJson = (song: Song): string => {
    const lines: string[] = ['{'];
    const entries = Object.entries(song as unknown as Record<string, unknown>);
    entries.forEach(([k, v], i) => {
      const comma = i < entries.length - 1 ? ',' : '';
      if (Array.isArray(v)) {
        lines.push(`  ${JSON.stringify(k)}: [${v.map(x => JSON.stringify(x)).join(', ')}]${comma}`);
      } else {
        lines.push(`  ${JSON.stringify(k)}: ${JSON.stringify(v)}${comma}`);
      }
    });
    lines.push('}');
    return lines.join('\n');
  };

  copyJsonBtn.addEventListener('click', async () => {
    if (!currentSong) return;
    try {
      await navigator.clipboard.writeText(songToJson(currentSong));
      const orig = copyJsonBtn.textContent!;
      copyJsonBtn.textContent = 'Copied!';
      setTimeout(() => { copyJsonBtn.textContent = orig; }, 1400);
    } catch {
      copyJsonBtn.textContent = 'Failed';
      setTimeout(() => { copyJsonBtn.textContent = 'Copy JSON'; }, 1400);
    }
  });

  const closeBtn = document.createElement('button');
  closeBtn.textContent = 'Close';
  Object.assign(closeBtn.style, {
    padding: '8px 18px', background: '#553388', color: '#fff',
    border: '1px solid #aa77dd', borderRadius: '6px',
    cursor: 'pointer', font: 'inherit', fontWeight: '600',
  } as Partial<CSSStyleDeclaration>);
  closeBtn.addEventListener('click', close);
  footer.append(hint, copyJsonBtn, closeBtn);

  panel.append(header, list, tracker, footer);
  overlay.appendChild(panel);
  document.body.appendChild(overlay);

  // Touch-ghost-click guard (mirror of modalUtils.createModalShell): when
  // the modal mounts directly under the finger that opened it, mobile
  // browsers fire a synthesized `click` at that point AFTER mount which
  // lands on whatever modal element happens to be there (e.g. the first
  // track card). Swallow pointer/click on the panel for ~350ms.
  {
    const SWALLOW_MS = 350;
    const openedAt = performance.now();
    const swallow = (e: Event): void => {
      if (performance.now() - openedAt < SWALLOW_MS) {
        e.stopPropagation();
        if (e.type === 'click') e.preventDefault();
      }
    };
    panel.addEventListener('click', swallow, { capture: true });
    panel.addEventListener('pointerdown', swallow, { capture: true });
  }

  // ----- Reactive helpers -------------------------------------------------
  const refreshHint = (): void => {
    if (editMode) {
      if (currentSong && isUserIdx(currentIdx)) {
        const sc = currentScale();
        const scaleNote = sc.intervals.length === 0
          ? ''
          : `  \u00b7  snap: ${scaleRoot} ${sc.label}`;
        hint.textContent = `Click to add  \u00b7  drag up/down for pitch  \u00b7  scroll = \u00b11, Shift+scroll = octave  \u00b7  Shift-click clears${scaleNote}`;
      } else if (currentSong) {
        hint.textContent = 'Built-in track \u2014 click \u201cFork\u201d to make an editable copy.';
      } else {
        hint.textContent = 'Editor armed \u2014 select a track or click \u201cNew track\u201d below.';
      }
    } else {
      hint.textContent = 'Tap a track  \u00b7  cells show the 16-step pattern  \u00b7  Esc to close';
    }
  };
  const refreshMeta = (): void => {
    const total = builtins.length + userSongs.length;
    meta.textContent = currentSong
      ? `${moodFor(currentSong)}${isUserIdx(currentIdx) ? '  \u00b7  USER' : ''}`
      : `${total} tracks  \u00b7  ${builtins.length} built-in, ${userSongs.length} yours  \u00b7  16 steps / bar`;
    const hasSong = currentSong !== null;
    copyJsonBtn.disabled = !hasSong;
    Object.assign(copyJsonBtn.style, {
      background: hasSong ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.04)',
      color: hasSong ? '#ddd' : '#666',
      cursor: hasSong ? 'pointer' : 'default',
      borderColor: hasSong ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.1)',
    } as Partial<CSSStyleDeclaration>);
  };

  /** Read the current value at (channel, step). Returns 0 / '' when empty. */
  const cellValue = (
    song: UserSong, channel: 'lead' | 'bass' | 'drums', step: number,
  ): number | string => {
    if (channel === 'drums') return song.drums[step] ?? 0;
    if (channel === 'lead') return song.lead[step] ?? 0;
    return song.bass[step] ?? 0;
  };

  /** Write a value back into the song and update the cell visual. */
  const setCell = (
    cell: HTMLDivElement, song: UserSong, channel: 'lead' | 'bass' | 'drums',
    step: number, value: number | 'k' | 's' | 'h' | 0,
  ): void => {
    if (channel === 'drums') {
      song.drums[step] = value as 'k' | 's' | 'h' | 0;
    } else if (channel === 'lead') {
      song.lead[step] = value as number;
    } else {
      song.bass[step] = value as number;
    }
    UserSongs.update(song);
    paintCellVisual(cell, channel, value);
  };

  /** Clamp a pitch into a channel's allowed range. */
  const clampPitch = (channel: 'lead' | 'bass', n: number): number => {
    const r = PITCH_RANGE[channel];
    return Math.max(r.lo, Math.min(r.hi, n));
  };

  /** Pointer-down: empty cell → place a note at the channel's
   *  last-used pitch; filled cell → arm a drag-to-pitch operation that
   *  starts from the existing note and changes by ~1 semitone every
   *  10px of vertical movement.  Shift / right-click clears the cell.
   *  Inspired by BeepBox + Renoise. */
  const onCellPointerDown = (
    cell: HTMLDivElement, channel: 'lead' | 'bass' | 'drums', step: number, ev: PointerEvent,
  ): void => {
    if (!editMode || !currentSong || !isUserIdx(currentIdx)) return;
    if (ev.button !== 0 && ev.button !== 2) return;
    ev.preventDefault();
    const song = currentSong as UserSong;
    const clear = ev.shiftKey || ev.button === 2;
    if (clear) { setCell(cell, song, channel, step, 0); return; }

    const cur = cellValue(song, channel, step);
    if (channel === 'drums') {
      // Drums: cycle on click (off → k → s → h → off). Drag has no
      // continuous axis, so we let pointerup stay a no-op and just
      // advance one step per click.
      const idx = cur === 0 ? -1 : DRUM_CYCLE.indexOf(cur as 'k' | 's' | 'h');
      const next = idx === DRUM_CYCLE.length - 1 ? 0 : DRUM_CYCLE[idx + 1]!;
      if (next !== 0) lastPitch.drums = next;
      setCell(cell, song, channel, step, next);
      return;
    }

    // Lead / bass: place if empty, then start drag-to-pitch from the
    // (possibly-just-placed) value.
    let basePitch: number;
    if (cur === 0) {
      basePitch = snapToScale(channel, lastPitch[channel]);
      setCell(cell, song, channel, step, basePitch);
    } else {
      basePitch = cur as number;
    }
    const startY = ev.clientY;
    let lastApplied = basePitch;
    let didDrag = false;
    const PX_PER_SEMI = 8;
    try { cell.setPointerCapture(ev.pointerId); } catch { /* ignore */ }

    const onMove = (e: PointerEvent): void => {
      const dy = startY - e.clientY; // up = positive
      const semis = Math.round(dy / PX_PER_SEMI);
      const raw = clampPitch(channel, basePitch + semis);
      const next = snapToScale(channel, raw);
      if (next === lastApplied) return;
      didDrag = true;
      lastApplied = next;
      lastPitch[channel] = next;
      setCell(cell, song, channel, step, next);
    };
    const onUp = (e: PointerEvent): void => {
      cell.removeEventListener('pointermove', onMove);
      cell.removeEventListener('pointerup', onUp);
      cell.removeEventListener('pointercancel', onUp);
      try { cell.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
      // Click without drag on a filled cell = toggle off.
      if (!didDrag && cur !== 0) setCell(cell, song, channel, step, 0);
    };
    cell.addEventListener('pointermove', onMove);
    cell.addEventListener('pointerup', onUp);
    cell.addEventListener('pointercancel', onUp);
  };

  /** Wheel: scroll-to-change-pitch (Shift = octave). On empty cells the
   *  first tick places a note at the last-used pitch then moves from
   *  there — same behaviour as Renoise's pattern editor. */
  const onCellWheel = (
    cell: HTMLDivElement, channel: 'lead' | 'bass' | 'drums', step: number, ev: WheelEvent,
  ): void => {
    if (!editMode || !currentSong || !isUserIdx(currentIdx)) return;
    ev.preventDefault();
    const song = currentSong as UserSong;
    const dir = ev.deltaY < 0 ? 1 : -1;
    if (channel === 'drums') {
      const cur = song.drums[step] ?? 0;
      const idx = cur === 0 ? -1 : DRUM_CYCLE.indexOf(cur as 'k' | 's' | 'h');
      let nextIdx = idx + dir;
      if (nextIdx < -1) nextIdx = DRUM_CYCLE.length - 1;
      if (nextIdx >= DRUM_CYCLE.length) nextIdx = -1;
      const next = nextIdx < 0 ? 0 : DRUM_CYCLE[nextIdx]!;
      if (next !== 0) lastPitch.drums = next;
      setCell(cell, song, channel, step, next);
      return;
    }
    const cur = cellValue(song, channel, step) as number;
    const step12 = ev.shiftKey ? 12 : 1;
    const base = cur === 0 ? lastPitch[channel] : cur + dir * step12;
    const next = snapToScale(channel, clampPitch(channel, base));
    lastPitch[channel] = next;
    setCell(cell, song, channel, step, next);
  };

  /** Apply the cell's filled/empty appearance from a value. */
  const paintCellVisual = (
    cell: HTMLDivElement, channel: 'lead' | 'bass' | 'drums', v: number | string,
  ): void => {
    const accent = channel === 'lead' ? '#88ddff' : channel === 'bass' ? '#aa77dd' : '#ff77aa';
    const filled = v !== 0 && v !== '';
    const step = Number(cell.dataset.step ?? 0);
    const beat = step % 4 === 0;
    cell.style.background = filled
      ? `linear-gradient(180deg, ${accent}cc, ${accent}66)`
      : (beat ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.02)');
    cell.style.border = filled ? `1px solid ${accent}` : '1px solid rgba(255,255,255,0.06)';
    cell.style.color = filled ? '#fff' : 'rgba(255,255,255,0.25)';
    cell.style.textShadow = filled ? '0 1px 2px rgba(0,0,0,0.5)' : 'none';
    cell.textContent = filled
      ? (typeof v === 'number' ? midiToName(v) : v.toUpperCase())
      : '';
  };

  const renderTrackerRow = (
    channel: 'lead' | 'bass' | 'drums', label: string,
    notes: ReadonlyArray<number | string>, accent: string,
  ): { row: HTMLDivElement; cells: HTMLDivElement[] } => {
    const row = document.createElement('div');
    Object.assign(row.style, {
      display: 'grid',
      gridTemplateColumns: narrow ? `36px repeat(16, minmax(0, 1fr))` : `64px repeat(16, minmax(0, 1fr))`,
      gap: narrow ? '2px' : '3px', alignItems: 'stretch',
    } as Partial<CSSStyleDeclaration>);
    const lab = document.createElement('div');
    lab.textContent = label;
    Object.assign(lab.style, {
      display: 'flex', alignItems: 'center', justifyContent: 'flex-start',
      padding: '0 10px', fontSize: '11px', fontWeight: '700',
      letterSpacing: '0.1em', color: accent, opacity: '0.9',
      background: 'rgba(255,255,255,0.04)', borderRadius: '4px',
    } as Partial<CSSStyleDeclaration>);
    row.appendChild(lab);
    const cells: HTMLDivElement[] = [];
    const editing = editMode && isUserIdx(currentIdx);
    for (let i = 0; i < 16; i++) {
      const cell = document.createElement('div');
      cell.dataset.channel = channel;
      cell.dataset.step = String(i);
      Object.assign(cell.style, {
        height: '36px', borderRadius: '4px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '10px', fontWeight: '600',
        transition: 'box-shadow 80ms linear',
        cursor: editing ? 'pointer' : 'default',
        userSelect: 'none',
      } as Partial<CSSStyleDeclaration>);
      paintCellVisual(cell, channel, notes[i] ?? 0);
      if (editing) {
        cell.addEventListener('pointerdown', (ev) => onCellPointerDown(cell, channel, i, ev));
        cell.addEventListener('wheel', (ev) => onCellWheel(cell, channel, i, ev), { passive: false });
        cell.addEventListener('contextmenu', (ev) => ev.preventDefault());
        cell.title = channel === 'drums'
          ? 'Click to cycle  ·  scroll to change'
          : 'Click to add/remove  ·  drag up/down for pitch  ·  scroll = ±1, Shift+scroll = octave';
      }
      row.appendChild(cell);
      cells.push(cell);
    }
    return { row, cells };
  };

  /** Editor toolbar: rename / BPM / clear / delete (user tracks only). */
  const renderEditorToolbar = (song: UserSong): HTMLDivElement => {
    const bar = document.createElement('div');
    Object.assign(bar.style, { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' } as Partial<CSSStyleDeclaration>);

    const nameInput = document.createElement('input');
    nameInput.type = 'text'; nameInput.value = song.name;
    Object.assign(nameInput.style, {
      padding: '6px 10px', flex: '1', minWidth: '160px',
      background: 'rgba(0,0,0,0.35)', color: '#eee',
      border: '1px solid #3a2a55', borderRadius: '4px', font: 'inherit', fontSize: '13px',
    } as Partial<CSSStyleDeclaration>);
    nameInput.addEventListener('input', () => {
      song.name = nameInput.value || 'Untitled';
      UserSongs.update(song);
      nowTitle.textContent = song.name;
      const card = cards[currentIdx];
      if (card) {
        const t = card.querySelector('.jb-name') as HTMLElement | null;
        if (t) t.textContent = `\u2735 ${song.name}`;
      }
    });

    const bpmLabel = document.createElement('span');
    bpmLabel.textContent = 'BPM';
    Object.assign(bpmLabel.style, { fontSize: '10px', opacity: '0.7', letterSpacing: '0.1em', fontWeight: '700' } as Partial<CSSStyleDeclaration>);
    const bpmInput = document.createElement('input');
    bpmInput.type = 'number'; bpmInput.min = '40'; bpmInput.max = '220'; bpmInput.value = String(song.bpm);
    bpmInput.title = 'Click and drag up/down to change BPM';
    Object.assign(bpmInput.style, {
      width: '64px', padding: '6px 8px',
      background: 'rgba(0,0,0,0.35)', color: '#eee',
      border: '1px solid #3a2a55', borderRadius: '4px', font: 'inherit', fontSize: '13px',
      cursor: 'ns-resize',
    } as Partial<CSSStyleDeclaration>);
    const applyBpm = (v: number): void => {
      const clamped = Math.max(40, Math.min(220, Math.round(v)));
      if (clamped === song.bpm) return;
      song.bpm = clamped;
      bpmInput.value = String(clamped);
      UserSongs.update(song); refreshMeta();
      const card = cards[currentIdx];
      if (card) {
        const m = card.querySelector('.jb-mood') as HTMLElement | null;
        if (m) m.textContent = moodFor(song);
      }
    };
    bpmInput.addEventListener('input', () => {
      const v = Math.max(40, Math.min(220, Number(bpmInput.value) || song.bpm));
      applyBpm(v);
    });
    // Click-and-drag (vertical) to scrub BPM, like a tracker. Pointer-down
    // arms the drag; if the pointer actually moves we suppress the implicit
    // text-cursor placement so the input doesn't steal focus mid-scrub.
    bpmInput.addEventListener('pointerdown', (ev) => {
      if (ev.button !== 0) return;
      const startY = ev.clientY;
      const startBpm = song.bpm;
      let dragged = false;
      const PX_PER_BPM = 2;
      try { bpmInput.setPointerCapture(ev.pointerId); } catch { /* ignore */ }
      const onMove = (e: PointerEvent): void => {
        const dy = startY - e.clientY; // up = positive = faster
        const delta = Math.round(dy / PX_PER_BPM);
        if (delta === 0 && !dragged) return;
        if (Math.abs(dy) > 3) dragged = true;
        applyBpm(startBpm + delta);
      };
      const onUp = (e: PointerEvent): void => {
        bpmInput.removeEventListener('pointermove', onMove);
        bpmInput.removeEventListener('pointerup', onUp);
        bpmInput.removeEventListener('pointercancel', onUp);
        try { bpmInput.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
        if (dragged) {
          // We hijacked the click — keep focus off so subsequent scrolls
          // don't accidentally edit the number.
          bpmInput.blur();
          ev.preventDefault();
        }
      };
      bpmInput.addEventListener('pointermove', onMove);
      bpmInput.addEventListener('pointerup', onUp);
      bpmInput.addEventListener('pointercancel', onUp);
    });

    const clearBtn = document.createElement('button');
    clearBtn.textContent = 'Clear';
    Object.assign(clearBtn.style, {
      padding: '6px 12px', background: '#332244', color: '#eee',
      border: '1px solid #553366', borderRadius: '4px', cursor: 'pointer', font: 'inherit',
    } as Partial<CSSStyleDeclaration>);
    clearBtn.addEventListener('click', () => {
      song.lead = new Array(16).fill(0);
      song.bass = new Array(16).fill(0);
      song.drums = new Array(16).fill(0);
      UserSongs.update(song);
      buildTracker(song);
    });

    const delBtn = document.createElement('button');
    delBtn.textContent = 'Delete';
    Object.assign(delBtn.style, {
      padding: '6px 12px', background: '#4a1f2a', color: '#fff',
      border: '1px solid #aa3344', borderRadius: '4px', cursor: 'pointer', font: 'inherit',
    } as Partial<CSSStyleDeclaration>);
    delBtn.addEventListener('click', () => {
      if (!confirm(`Delete \u201c${song.name}\u201d?`)) return;
      UserSongs.remove(song.id);
      userSongs = UserSongs.listUserSongs();
      stop();
      currentSong = null; currentIdx = -1;
      rebuildList();
      tracker.innerHTML = '';
      const empty = document.createElement('div');
      empty.textContent = 'Select a track to view its tracker timeline.';
      Object.assign(empty.style, { opacity: '0.6', padding: '20px', fontStyle: 'italic' } as Partial<CSSStyleDeclaration>);
      tracker.appendChild(empty);
      nowTitle.textContent = '\u2014 Pick a track \u2014';
      refreshMeta();
      refreshHint();
    });

    bar.append(nameInput, bpmLabel, bpmInput, clearBtn, delBtn);

    // ----- Gain (master volume) -----
    // The synth's "comfortable" master gain band is 0.10..0.15 (anything
    // higher distorts on punchy drum hits, anything lower is inaudible
    // over the SFX bus). Expose a 0..1 slider mapped linearly into that
    // band so the user gets the full perceived range.
    const GAIN_LO = 0.10;
    const GAIN_HI = 0.15;
    const gainToUi = (g: number): number => {
      const clamped = Math.max(GAIN_LO, Math.min(GAIN_HI, g));
      return (clamped - GAIN_LO) / (GAIN_HI - GAIN_LO);
    };
    const uiToGain = (u: number): number => GAIN_LO + Math.max(0, Math.min(1, u)) * (GAIN_HI - GAIN_LO);

    const gainRow = document.createElement('div');
    Object.assign(gainRow.style, {
      display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
    } as Partial<CSSStyleDeclaration>);
    const gainLabel = document.createElement('span');
    gainLabel.textContent = 'GAIN';
    Object.assign(gainLabel.style, {
      fontSize: '10px', opacity: '0.7', letterSpacing: '0.1em', fontWeight: '700',
    } as Partial<CSSStyleDeclaration>);
    const gainSlider = document.createElement('input');
    gainSlider.type = 'range'; gainSlider.min = '0'; gainSlider.max = '1'; gainSlider.step = '0.01';
    gainSlider.value = String(gainToUi(song.gain ?? 0.12));
    Object.assign(gainSlider.style, {
      flex: '1', minWidth: '120px', accentColor: '#aa77dd', cursor: 'pointer',
    } as Partial<CSSStyleDeclaration>);
    const gainReadout = document.createElement('span');
    Object.assign(gainReadout.style, {
      fontSize: '11px', opacity: '0.7', minWidth: '32px', textAlign: 'right',
      fontVariantNumeric: 'tabular-nums',
    } as Partial<CSSStyleDeclaration>);
    const updateGainReadout = (): void => {
      gainReadout.textContent = `${Math.round(Number(gainSlider.value) * 100)}%`;
    };
    updateGainReadout();
    gainSlider.addEventListener('input', () => {
      song.gain = uiToGain(Number(gainSlider.value));
      UserSongs.update(song);
      updateGainReadout();
      if (previewMusic) previewMusic.refreshGain();
    });
    gainRow.append(gainLabel, gainSlider, gainReadout);

    // Second row: scale filter (root + mode). Constrains scroll/drag
    // pitch changes to a tonality. "Chromatic" is the no-op default.
    const scaleRow = document.createElement('div');
    Object.assign(scaleRow.style, {
      display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
      width: '100%',
    } as Partial<CSSStyleDeclaration>);

    const scaleLabel = document.createElement('span');
    scaleLabel.textContent = 'SCALE';
    Object.assign(scaleLabel.style, {
      fontSize: '10px', opacity: '0.7', letterSpacing: '0.1em', fontWeight: '700',
    } as Partial<CSSStyleDeclaration>);

    const styleSelect = (s: HTMLSelectElement): void => {
      Object.assign(s.style, {
        padding: '6px 8px',
        background: 'rgba(0,0,0,0.35)', color: '#eee',
        border: '1px solid #3a2a55', borderRadius: '4px',
        font: 'inherit', fontSize: '13px', cursor: 'pointer',
      } as Partial<CSSStyleDeclaration>);
    };

    const rootSelect = document.createElement('select');
    styleSelect(rootSelect);
    rootSelect.title = 'Root note';
    for (const r of ROOTS) {
      const opt = document.createElement('option');
      opt.value = r; opt.textContent = r;
      if (r === scaleRoot) opt.selected = true;
      rootSelect.appendChild(opt);
    }
    rootSelect.disabled = scaleKey === 'none';
    rootSelect.style.opacity = rootSelect.disabled ? '0.45' : '1';
    rootSelect.addEventListener('change', () => {
      scaleRoot = rootSelect.value as RootName;
      persistScale();
      resnapCurrentSong();
    });

    const scaleSelect = document.createElement('select');
    styleSelect(scaleSelect);
    scaleSelect.title = 'Scale (filters scroll/drag)';
    for (const s of SCALES) {
      const opt = document.createElement('option');
      opt.value = s.key; opt.textContent = s.label;
      if (s.key === scaleKey) opt.selected = true;
      scaleSelect.appendChild(opt);
    }
    scaleSelect.addEventListener('change', () => {
      scaleKey = scaleSelect.value;
      const off = scaleKey === 'none';
      rootSelect.disabled = off;
      rootSelect.style.opacity = off ? '0.45' : '1';
      persistScale();
      refreshHint();
      resnapCurrentSong();
    });

    scaleRow.append(scaleLabel, rootSelect, scaleSelect);

    const wrap = document.createElement('div');
    Object.assign(wrap.style, { display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' } as Partial<CSSStyleDeclaration>);
    wrap.append(bar, gainRow, scaleRow);
    return wrap as unknown as HTMLDivElement;
  };

  const buildTracker = (song: Song): void => {
    tracker.innerHTML = '';

    if (editMode && isUserIdx(currentIdx)) {
      tracker.appendChild(renderEditorToolbar(song as UserSong));
    } else if (editMode && currentSong) {
      const forkPrompt = document.createElement('div');
      Object.assign(forkPrompt.style, {
        padding: '10px 12px', borderRadius: '8px',
        background: 'rgba(170,119,221,0.12)',
        border: '1px solid #6644aa',
        display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'space-between',
      } as Partial<CSSStyleDeclaration>);
      const pmsg = document.createElement('div');
      pmsg.textContent = 'Built-in tracks are read-only. Fork a copy to start editing.';
      Object.assign(pmsg.style, { fontSize: '12px' } as Partial<CSSStyleDeclaration>);
      const forkBtn = document.createElement('button');
      forkBtn.textContent = 'Fork to user track';
      Object.assign(forkBtn.style, {
        padding: '6px 14px', background: '#553388', color: '#fff',
        border: '1px solid #aa77dd', borderRadius: '4px', cursor: 'pointer', font: 'inherit', fontWeight: '600',
      } as Partial<CSSStyleDeclaration>);
      forkBtn.addEventListener('click', () => {
        UserSongs.fork(song);
        userSongs = UserSongs.listUserSongs();
        rebuildList();
        playIdx(builtins.length + userSongs.length - 1);
      });
      forkPrompt.append(pmsg, forkBtn);
      tracker.appendChild(forkPrompt);
    }

    const ruler = document.createElement('div');
    Object.assign(ruler.style, {
      display: 'grid',
      gridTemplateColumns: narrow ? `36px repeat(16, minmax(0, 1fr))` : `64px repeat(16, minmax(0, 1fr))`,
      gap: narrow ? '2px' : '3px',
    } as Partial<CSSStyleDeclaration>);
    const rl = document.createElement('div');
    rl.textContent = 'STEP';
    Object.assign(rl.style, { fontSize: '9px', opacity: '0.5', padding: '0 10px', letterSpacing: '0.1em' } as Partial<CSSStyleDeclaration>);
    ruler.appendChild(rl);
    stepHeaderCells = [];
    for (let i = 0; i < 16; i++) {
      const c = document.createElement('div');
      c.textContent = String(i + 1);
      Object.assign(c.style, {
        textAlign: 'center', fontSize: '9px', opacity: i % 4 === 0 ? '0.85' : '0.4',
        fontWeight: i % 4 === 0 ? '700' : '400',
        padding: '4px 0', borderRadius: '3px',
        transition: 'background 80ms linear, color 80ms linear',
      } as Partial<CSSStyleDeclaration>);
      ruler.appendChild(c);
      stepHeaderCells.push(c);
    }
    tracker.appendChild(ruler);

    const lead = renderTrackerRow('lead', 'LEAD', song.lead, '#88ddff');
    const bass = renderTrackerRow('bass', 'BASS', song.bass, '#aa77dd');
    const drums = renderTrackerRow('drums', 'DRUMS', song.drums.map((d: number | string) => d || 0), '#ff77aa');
    tracker.append(lead.row, bass.row, drums.row);
    leadCells = lead.cells;
    bassCells = bass.cells;
    drumCells = drums.cells;

    const cap = document.createElement('div');
    cap.textContent = `${moodFor(song)}  \u00b7  16 steps / bar  \u00b7  cells = data-channel/data-step`;
    Object.assign(cap.style, { marginTop: '6px', fontSize: '11px', opacity: '0.55' } as Partial<CSSStyleDeclaration>);
    tracker.appendChild(cap);
  };

  const refreshCardStyles = (): void => {
    for (const c of cards) {
      const playing = c.dataset.playing === '1';
      c.style.background = playing
        ? 'linear-gradient(135deg, #553388, #aa77dd)'
        : '#1a1030';
      c.style.borderColor = playing ? '#ddbbff' : '#3a2a55';
      c.style.boxShadow = playing ? '0 0 14px rgba(170,119,221,0.5)' : 'none';
      const ico = c.querySelector('.jb-ico') as HTMLElement | null;
      if (ico) ico.textContent = playing ? '\u25A0' : '\u25B6';
    }
    transport.textContent = currentIdx >= 0 ? '\u25A0' : '\u25B6';
  };

  const stop = (): void => {
    if (previewMusic) { previewMusic.stop(); previewMusic = null; }
    currentIdx = -1;
    currentSong = null;
    for (const c of cards) c.dataset.playing = '';
    refreshCardStyles();
    refreshMeta();
    refreshHint();
  };

  const playIdx = (i: number): void => {
    const songs = allSongs();
    if (i === currentIdx) { stop(); return; }
    if (previewMusic) { previewMusic.stop(); previewMusic = null; }
    const song = songs[i]!;
    const m = new Music(song);
    m.getAnalyser();
    m.start();
    previewMusic = m;
    currentIdx = i;
    currentSong = song;
    nowTitle.textContent = song.name;
    buildTracker(song);
    for (let k = 0; k < cards.length; k++) cards[k]!.dataset.playing = k === i ? '1' : '';
    refreshCardStyles();
    refreshMeta();
    refreshHint();
  };

  /** Rebuild the left-side list. Called on init and on user-song add/remove. */
  const rebuildList = (): void => {
    list.innerHTML = '';
    cards = [];
    const songs = allSongs();
    const addSection = (label: string): void => {
      const s = document.createElement('div');
      s.textContent = label;
      Object.assign(s.style, {
        fontSize: '10px', opacity: '0.55', letterSpacing: '0.15em',
        padding: '8px 4px 4px', fontWeight: '700',
      } as Partial<CSSStyleDeclaration>);
      list.appendChild(s);
    };
    const addCard = (s: Song, i: number, isUser: boolean): void => {
      const card = document.createElement('button');
      card.dataset.playing = i === currentIdx ? '1' : '';
      Object.assign(card.style, {
        textAlign: 'left', padding: '8px 10px',
        background: '#1a1030', color: '#eee',
        border: '1px solid #3a2a55', borderRadius: '6px',
        cursor: 'pointer', font: 'inherit',
        display: 'flex', alignItems: 'center', gap: '8px',
        transition: 'background 120ms ease, border-color 120ms ease, box-shadow 120ms ease',
      } as Partial<CSSStyleDeclaration>);
      const ico = document.createElement('span');
      ico.className = 'jb-ico';
      ico.textContent = '\u25B6';
      Object.assign(ico.style, {
        width: '22px', height: '22px',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(255,255,255,0.08)',
        border: '1px solid rgba(255,255,255,0.2)',
        borderRadius: '50%', fontSize: '10px', flex: '0 0 auto',
      } as Partial<CSSStyleDeclaration>);
      const txt = document.createElement('div');
      Object.assign(txt.style, { minWidth: '0', flex: '1' } as Partial<CSSStyleDeclaration>);
      const tname = document.createElement('div');
      tname.className = 'jb-name';
      tname.textContent = isUser ? `\u2735 ${s.name}` : `${String(i + 1).padStart(2, '0')}  ${s.name}`;
      Object.assign(tname.style, {
        fontWeight: '700', fontSize: '12px',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      } as Partial<CSSStyleDeclaration>);
      const tmood = document.createElement('div');
      tmood.className = 'jb-mood';
      tmood.textContent = moodFor(s);
      Object.assign(tmood.style, { fontSize: '10px', opacity: '0.6', marginTop: '1px' } as Partial<CSSStyleDeclaration>);
      txt.append(tname, tmood);
      card.append(ico, txt);
      card.addEventListener('click', () => playIdx(i));
      list.appendChild(card);
      cards.push(card);
    };

    addSection('BUILT-IN');
    for (let i = 0; i < songs.length; i++) {
      if (i === builtins.length) addSection('YOURS');
      addCard(songs[i]!, i, isUserIdx(i));
    }
    if (userSongs.length === 0) addSection('YOURS');

    const newBtn = document.createElement('button');
    newBtn.textContent = '+ New track';
    Object.assign(newBtn.style, {
      marginTop: '4px',
      padding: '8px 10px', background: 'rgba(170,119,221,0.15)', color: '#ddbbff',
      border: '1px dashed #6644aa', borderRadius: '6px',
      cursor: 'pointer', font: 'inherit', fontWeight: '600', fontSize: '12px',
    } as Partial<CSSStyleDeclaration>);
    newBtn.addEventListener('click', () => {
      UserSongs.createBlank();
      userSongs = UserSongs.listUserSongs();
      rebuildList();
      playIdx(builtins.length + userSongs.length - 1);
    });
    list.appendChild(newBtn);

    refreshCardStyles();
  };

  const setEditMode = (on: boolean): void => {
    editMode = on;
    editBtn.style.background = on ? 'rgba(170,119,221,0.35)' : 'rgba(255,255,255,0.08)';
    editBtn.style.borderColor = on ? '#ddbbff' : 'rgba(255,255,255,0.25)';
    editBtn.textContent = on ? '\u270E Editing' : '\u270E Edit';
    if (currentSong) buildTracker(currentSong);
    refreshHint();
    refreshMeta();
  };
  editBtn.addEventListener('click', () => setEditMode(!editMode));

  transport.addEventListener('click', () => {
    if (currentIdx >= 0) stop();
    else if (cards.length > 0) playIdx(0);
  });

  // Initial render
  rebuildList();
  refreshMeta();
  refreshHint();

  // ----- Visualizer + playhead loop ---------------------------------------
  const ctx = eqCanvas.getContext('2d');
  let lastStep = -1;
  const draw = (): void => {
    rafId = requestAnimationFrame(draw);

    if (previewMusic && leadCells.length === 16) {
      const s = previewMusic.getCurrentStep();
      if (s !== lastStep) {
        const apply = (cells: HTMLDivElement[], idx: number, on: boolean): void => {
          const c = cells[idx]; if (!c) return;
          c.style.boxShadow = on ? '0 0 12px rgba(255,255,255,0.7), inset 0 0 6px rgba(255,255,255,0.4)' : '';
          c.style.outline = on ? '1px solid rgba(255,255,255,0.9)' : '';
        };
        if (lastStep >= 0) {
          apply(leadCells, lastStep, false);
          apply(bassCells, lastStep, false);
          apply(drumCells, lastStep, false);
          const h = stepHeaderCells[lastStep]; if (h) { h.style.background = ''; h.style.color = ''; }
        }
        if (s >= 0) {
          apply(leadCells, s, true);
          apply(bassCells, s, true);
          apply(drumCells, s, true);
          const h = stepHeaderCells[s];
          if (h) { h.style.background = 'rgba(255,255,255,0.18)'; h.style.color = '#fff'; }
        }
        lastStep = s;
      }
    } else if (lastStep >= 0) {
      const apply = (cells: HTMLDivElement[], idx: number): void => {
        const c = cells[idx]; if (!c) return;
        c.style.boxShadow = ''; c.style.outline = '';
      };
      apply(leadCells, lastStep);
      apply(bassCells, lastStep);
      apply(drumCells, lastStep);
      const h = stepHeaderCells[lastStep]; if (h) { h.style.background = ''; h.style.color = ''; }
      lastStep = -1;
    }

    if (!ctx) return;
    const W = eqCanvas.width, H = eqCanvas.height;
    ctx.fillStyle = 'rgba(0,0,0,0.35)';
    ctx.fillRect(0, 0, W, H);
    const analyser = previewMusic ? previewMusic.getAnalyser() : null;
    if (!analyser) {
      const t = performance.now() / 1000;
      const bars = 16;
      const bw = W / bars;
      for (let i = 0; i < bars; i++) {
        const a = 0.25 + 0.25 * Math.sin(t * 2 + i * 0.6);
        const h = (H * 0.18) * a;
        ctx.fillStyle = `rgba(170,119,221,${0.35 + a * 0.3})`;
        ctx.fillRect(i * bw + 1, H - h, bw - 2, h);
      }
      return;
    }
    const n = analyser.frequencyBinCount;
    const data = new Uint8Array(n);
    analyser.getByteFrequencyData(data);
    const bw = W / n;
    for (let i = 0; i < n; i++) {
      const v = data[i]! / 255;
      const h = Math.max(2, v * H);
      const grad = ctx.createLinearGradient(0, H, 0, H - h);
      grad.addColorStop(0, '#ff77cc');
      grad.addColorStop(0.6, '#aa77dd');
      grad.addColorStop(1, '#88ddff');
      ctx.fillStyle = grad;
      ctx.fillRect(i * bw + 1, H - h, bw - 2, h);
    }
  };
  draw();

  const keyHandler = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
    }
  };
  document.addEventListener('keydown', keyHandler);

  return { close };
}
